import type { ApiNameType } from '@/constants';
import { useClientConfig } from '@/hooks';
import { useSiteContext } from '@/providers';
import { useConfigStore } from '@/stores';

import { resolveApiBaseUrlFromSources } from './getApiUrl/getApiUrl.functions.ts';

/**
 * Get the full base API URL for the current site.
 * Normalizes the URL by removing trailing slashes.
 *
 * Throws (after `console.error`) when no base can be resolved (except portable mode without a
 * selected site for non-cloud APIs, which returns `undefined`). Callers must treat a missing base
 * as “no fetch” (disable queries / guard mutations). Mount under {@link AccessibleSitesProvider}
 * after runtime `/config` has finished loading so resolution inputs exist.
 *
 * `useSiteContext` has a default (empty) value, so hooks that run in `AccessibleSitesProvider`
 * (above `SiteContext`) see no `currentSite`. They fall back to `defaultSite.apis` from the config
 * store. In portable mode there is no `defaultSite`, so hosted cloud base URLs come from bundled
 * `client_config` for cloud API ids.
 *
 * @param baseApiRoute - API name (e.g., API_NAMES.servicesApi).
 * @returns Full base API URL, or `undefined` in portable mode when no site is selected yet for a
 *   non-cloud (edge) API.
 */
export const useGetApiUrl = (baseApiRoute: ApiNameType): string | undefined => {
  const { currentSite } = useSiteContext();
  const clientConfig = useClientConfig();
  const { defaultSite, portableMode } = useConfigStore();

  return resolveApiBaseUrlFromSources({
    baseApiRoute,
    currentSiteApis: currentSite?.apis,
    defaultSiteApis: defaultSite?.apis,
    portableMode,
    clientApis: clientConfig.apis,
  });
};
